/**
 * Recession + strong-expansion probability chart.
 *
 * Powers the GDP-view "Recession" selector option. Backend endpoint is
 * /api/economic-outlook/recession?country=<>&timeframe=<>; rows carry
 * recessionprobability (0..1) and strongrecoveryprobability (0..1) fields,
 * which we plot as vertical percentage bars with 7-day rolling average
 * smoothing.
 */
import { useMemo } from 'react';
import type { Data, Layout } from 'plotly.js';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import { getRecessionShapes } from '@/lib/plotly/recessionShapes';
import {
  buildRecessionLayout,
  buildRecessionTraces,
} from '@/lib/plotly/recessionChart';
import { shortName } from '../lib/countries';
import type { CountryIso, RecessionRow } from '../types';

export interface RecessionChartProps {
  country: CountryIso;
  rows: RecessionRow[];
}

export default function RecessionChart({ country, rows }: RecessionChartProps): JSX.Element {
  const { traces, layout, hasData } = useMemo(() => {
    const { x, traces: builtTraces, firstDate, lastDate } = buildRecessionTraces(rows);
    if (builtTraces.length === 0 || firstDate === null || lastDate === null) {
      return {
        traces: [] as Data[],
        layout: {} as Partial<Layout>,
        hasData: false,
      };
    }

    // Add one month gap to the right when sizing the recession-band filter.
    const xRangeEnd = new Date(lastDate);
    xRangeEnd.setMonth(xRangeEnd.getMonth() + 1);

    const titleText = `${shortName(country)}: Real-time Scenario Recession Probabilities (12m ahead, %)`;

    // Only show recession bands for US.
    const shapes =
      country === 'US'
        ? getRecessionShapes({
            plotXRange: [new Date(firstDate).toISOString(), xRangeEnd.toISOString()],
          })
        : [];

    // Detail-page sizing: 16pt title at y:1.04, default top margin, and the
    // right-side mirrored `yaxis2`. Title lives in Plotly (no DOM h3
    // wrapper), so do not add `<ChartCard title>` for this chart.
    const composed = buildRecessionLayout({
      title: titleText,
      x,
      shapes,
      titleFontSize: 16,
      titleY: 1.04,
      includeYAxis2: true,
    });

    return {
      traces: builtTraces,
      layout: composed,
      hasData: true,
    };
  }, [rows, country]);

  if (!hasData) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-[color:var(--text-dark)] opacity-70">
        No recession probability data available
      </div>
    );
  }

  return (
    // Fill the parent slot (`h-full`) rather than hardcoding 550px. The
    // EconomicOutlookPage chart slot is a 550px box and wraps this chart in a
    // `<div className="h-full">`; a literal 550px here double-counts against
    // that box and left the chart short of the card edges ("strange
    // adjustment" / empty space). `h-full` makes the Plotly canvas track the
    // card's inner content box exactly, the same way the dashboard recession
    // mini-chart already fills its wrapper.
    <div className="h-full w-full overflow-hidden">
      <PlotlyChart data={traces} layout={layout} />
    </div>
  );
}
